// Whitelist allowed characters after variable substitution
const SAFE_EXPR = /^[0-9+\-*/().\s]+$/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function tokenize(expression: string): string[] {
  const tokens: string[] = [];
  let i = 0;

  while (i < expression.length) {
    const char = expression[i];

    if (/\s/.test(char)) {
      i += 1;
      continue;
    }

    if (/[+\-*/()]/.test(char)) {
      tokens.push(char);
      i += 1;
      continue;
    }

    if (/[0-9.]/.test(char)) {
      let j = i + 1;
      while (j < expression.length && /[0-9.]/.test(expression[j])) {
        j += 1;
      }
      const rawNumber = expression.slice(i, j);
      if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(rawNumber)) {
        return [];
      }
      tokens.push(rawNumber);
      i = j;
      continue;
    }

    return [];
  }

  return tokens;
}

class ArithmeticParser {
  private readonly tokens: string[];
  private index = 0;

  constructor(tokens: string[]) {
    this.tokens = tokens;
  }

  parse(): number {
    const value = this.parseExpression();
    if (this.index < this.tokens.length) {
      throw new Error('Unexpected trailing token');
    }
    return value;
  }

  private peek(): string | undefined {
    return this.tokens[this.index];
  }

  private consume(expected?: string): string {
    const token = this.tokens[this.index];
    if (!token) throw new Error('Unexpected end of expression');
    if (expected && token !== expected) {
      throw new Error(`Expected ${expected}, got ${token}`);
    }
    this.index += 1;
    return token;
  }

  private parseExpression(): number {
    let value = this.parseTerm();

    while (true) {
      const token = this.peek();
      if (token === '+' || token === '-') {
        this.consume();
        const rhs = this.parseTerm();
        value = token === '+' ? value + rhs : value - rhs;
        continue;
      }
      break;
    }

    return value;
  }

  private parseTerm(): number {
    let value = this.parseFactor();

    while (true) {
      const token = this.peek();
      if (token === '*' || token === '/') {
        this.consume();
        const rhs = this.parseFactor();
        if (token === '/' && rhs === 0) {
          return Number.NaN;
        }
        value = token === '*' ? value * rhs : value / rhs;
        continue;
      }
      break;
    }

    return value;
  }

  private parseFactor(): number {
    const token = this.peek();
    if (!token) throw new Error('Unexpected end of expression');

    if (token === '+') {
      this.consume('+');
      return this.parseFactor();
    }
    if (token === '-') {
      this.consume('-');
      return -this.parseFactor();
    }
    if (token === '(') {
      this.consume('(');
      const value = this.parseExpression();
      this.consume(')');
      return value;
    }

    this.consume();
    const numberValue = Number(token);
    if (!Number.isFinite(numberValue)) {
      throw new Error(`Invalid number token: ${token}`);
    }
    return numberValue;
  }
}

function evaluateArithmeticExpression(expression: string): number {
  const tokens = tokenize(expression);
  if (!tokens.length) return Number.NaN;
  try {
    const parser = new ArithmeticParser(tokens);
    const value = parser.parse();
    return Number.isFinite(value) ? value : Number.NaN;
  } catch {
    return Number.NaN;
  }
}

export function coerceNumber(v: unknown): number {
  if (v === undefined || v === null || v === '') return 0;
  const n = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Replace variables (e.g. a,b,c) in `expression` with numeric values from `vars`,
 * then evaluate using Function(...). We check the final string against SAFE_EXPR.
 */
export function evalExpression(expression: string, vars: Record<string, number>): number {
  let expr = expression;
  for (const [sym, val] of Object.entries(vars)) {
    // replace whole-word occurrences of variable name
    expr = expr.replace(new RegExp(`\\b${escapeRegExp(sym)}\\b`, 'g'), String(val));
  }
  if (!SAFE_EXPR.test(expr)) return NaN;
  return evaluateArithmeticExpression(expr);
}

export function clampRound(n: number, round: 'none' | 'int' = 'none', min?: number, max?: number) {
  let x = n;
  if (round === 'int') x = Math.round(x);
  if (typeof min === 'number') x = Math.max(min, x);
  if (typeof max === 'number') x = Math.min(max, x);
  return x;
}

/** Extract instance prefix like "12-" from a question name "12-fraud-investigations-ongoing". */
export function getInstancePrefix(questionName: string): string {
  const m = questionName.match(/^(\d+)-/); // only digits followed by '-' at the start
  return m ? m[0] : '';
}
